import type { Vec2, Ring } from './types';

function distance(a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}

function normalize(v: Vec2): Vec2 {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1]);
  if (len < 1e-10) return [1, 0];
  return [v[0] / len, v[1] / len];
}

function add(a: Vec2, b: Vec2): Vec2 {
  return [a[0] + b[0], a[1] + b[1]];
}

function scale(v: Vec2, s: number): Vec2 {
  return [v[0] * s, v[1] * s];
}

function perp(v: Vec2): Vec2 {
  return [-v[1], v[0]];
}

export function polylineLength(pts: readonly Vec2[]): number {
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    len += distance(pts[i - 1]!, pts[i]!);
  }
  return len;
}

export function resampleByDistance(pts: readonly Vec2[], step: number): readonly Vec2[] {
  if (pts.length < 2) return [...pts];

  const result: Vec2[] = [pts[0]!];
  let remaining = step;
  let i = 0;

  while (i < pts.length - 1) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const segLen = distance(a, b);

    if (segLen < 1e-10) {
      i++;
      continue;
    }

    if (remaining <= segLen) {
      const t = remaining / segLen;
      const p: Vec2 = [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
      result.push(p);
      remaining = step;
      pts = [p, ...pts.slice(i + 1)];
      i = 0;
    } else {
      remaining -= segLen;
      i++;
    }
  }

  const last = pts[pts.length - 1]!;
  const prev = result[result.length - 1]!;
  if (distance(prev, last) > 1e-6) {
    result.push(last);
  }

  return result;
}

export function tangentsAt(pts: readonly Vec2[]): readonly Vec2[] {
  if (pts.length < 2) return pts.map(() => [1, 0] as Vec2);

  const tangents: Vec2[] = [];

  for (let i = 0; i < pts.length; i++) {
    let tangent: Vec2;
    if (i === 0) {
      tangent = normalize([pts[1]![0] - pts[0]![0], pts[1]![1] - pts[0]![1]]);
    } else if (i === pts.length - 1) {
      const n = pts.length;
      tangent = normalize([pts[n - 1]![0] - pts[n - 2]![0], pts[n - 1]![1] - pts[n - 2]![1]]);
    } else {
      const prev = pts[i - 1]!;
      const next = pts[i + 1]!;
      tangent = normalize([next[0] - prev[0], next[1] - prev[1]]);
    }
    tangents.push(tangent);
  }

  return tangents;
}

export function offsetPolyline(
  pts: readonly Vec2[],
  offsets: readonly number[],
  miterLimit: number
): readonly Vec2[] {
  if (pts.length < 2) return [...pts];

  const result: Vec2[] = [];

  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!;
    const offset = offsets[i] ?? offsets[offsets.length - 1]!;

    if (i > 0 && i < pts.length - 1) {
      const prev = pts[i - 1]!;
      const next = pts[i + 1]!;

      const d1 = normalize([p[0] - prev[0], p[1] - prev[1]]);
      const d2 = normalize([next[0] - p[0], next[1] - p[1]]);

      const n1 = perp(d1);
      const n2 = perp(d2);

      const bisector = normalize(add(n1, n2));

      const dot = d1[0] * d2[0] + d1[1] * d2[1];
      const cosHalf = Math.sqrt((1 + dot) / 2);

      const miter = cosHalf > 1e-6 ? Math.min(1 / cosHalf, miterLimit) : miterLimit;
      result.push(add(p, scale(bisector, offset * miter)));
    } else if (i === 0) {
      const next = pts[1]!;
      const d = normalize([next[0] - p[0], next[1] - p[1]]);
      const n = perp(d);
      result.push(add(p, scale(n, offset)));
    } else {
      const prev = pts[i - 1]!;
      const d = normalize([p[0] - prev[0], p[1] - prev[1]]);
      const n = perp(d);
      result.push(add(p, scale(n, offset)));
    }
  }

  return result;
}

// Counter-clockwise like every other outer ring: a clockwise strip cancels the water it overlaps
// in a nonzero union.
export function stripPolygon(center: readonly Vec2[], widths: readonly number[]): Ring {
  if (center.length < 2) return [];

  const halfWidths = widths.map((w) => w / 2);
  const left = offsetPolyline(center, halfWidths, 2.0);
  const rightWidths = halfWidths.map((w) => -w);
  const right = offsetPolyline(center, rightWidths, 2.0);

  return [...right, ...left.slice().reverse()];
}

export function pointAtDistance(
  pts: readonly Vec2[],
  s: number
): { readonly point: Vec2; readonly tangent: Vec2 } {
  if (pts.length < 2) {
    return { point: pts[0] ?? [0, 0], tangent: [1, 0] };
  }

  let accumulated = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const segLen = distance(a, b);

    if (accumulated + segLen >= s || i === pts.length - 2) {
      const t = segLen > 1e-10 ? Math.max(0, Math.min(1, (s - accumulated) / segLen)) : 0;
      const point: Vec2 = [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
      const tangent = normalize([b[0] - a[0], b[1] - a[1]]);
      return { point, tangent };
    }

    accumulated += segLen;
  }

  const last = pts[pts.length - 1]!;
  const prev = pts[pts.length - 2]!;
  return { point: last, tangent: normalize([last[0] - prev[0], last[1] - prev[1]]) };
}

export function closestPointOnPolyline(
  pts: readonly Vec2[],
  p: Vec2
): {
  readonly point: Vec2;
  readonly distance: number;
  readonly segmentIndex: number;
  readonly t: number;
  readonly tangent: Vec2;
} {
  if (pts.length === 0) {
    return { point: p, distance: 0, segmentIndex: 0, t: 0, tangent: [1, 0] };
  }
  if (pts.length === 1) {
    return {
      point: pts[0]!,
      distance: distance(p, pts[0]!),
      segmentIndex: 0,
      t: 0,
      tangent: [1, 0],
    };
  }

  let bestDist = Infinity;
  let bestPoint: Vec2 = pts[0]!;
  let bestIndex = 0;
  let bestT = 0;

  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const ab: Vec2 = [b[0] - a[0], b[1] - a[1]];
    const ap: Vec2 = [p[0] - a[0], p[1] - a[1]];
    const lenSq = ab[0] * ab[0] + ab[1] * ab[1];

    let t = 0;
    if (lenSq > 1e-10) {
      t = Math.max(0, Math.min(1, (ap[0] * ab[0] + ap[1] * ab[1]) / lenSq));
    }

    const closest: Vec2 = [a[0] + t * ab[0], a[1] + t * ab[1]];
    const dist = distance(p, closest);

    if (dist < bestDist) {
      bestDist = dist;
      bestPoint = closest;
      bestIndex = i;
      bestT = t;
    }
  }

  const a = pts[bestIndex]!;
  const b = pts[bestIndex + 1]!;
  const tangent = normalize([b[0] - a[0], b[1] - a[1]]);

  return { point: bestPoint, distance: bestDist, segmentIndex: bestIndex, t: bestT, tangent };
}
